import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback, useEffect, useRef } from 'react';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import { defineMessage, FormattedMessage, useIntl } from 'react-intl';

import { useTrigger } from '@atlassian/post-office-context/post-office-trigger';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { Text } from '@atlaskit/primitives/text';

import RovoSampleIllustrationLight from '../../../assets/twc-onboarding-spotlight.light.svg';

import {
	PlacementFrontend,
	type placementRenderProps,
} from '@post-office/browser--growth-galileo/jira-issue-view-project-plan-twc-onboarding';

import Image from '@atlaskit/image';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightMedia } from '@atlaskit/spotlight/media';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

import type { DismissEvent } from '@atlaskit/spotlight/types';

const imageAlt = defineMessage({
	id: 'jira-issue-view-project-plan-twc-onboarding.spotlight.image-alt',
	defaultMessage: 'A document that is being generated by Rovo.',
	description: 'Alt text for image in the body of the onboarding spotlight.',
});

// Frontend
const render = PlacementFrontend.createFrontendStage({
	type: 'render',

	validate: noValidation<placementRenderProps>(),

	Component({ dismiss, onClick, placement = 'right-end', sourceComponent = null }) {
		const { formatMessage } = useIntl();
		const { messageInstanceViewed } = useMessageInstanceEvent();
		const { onCtaActioned, onClosed } = usePostOfficeUserInteractions();
		const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

		const handleDismiss = useCallback(
			(dismissEvent: DismissEvent) => {
				let dismissMethod: string;
				if (dismissEvent instanceof MouseEvent) dismissMethod = 'click_outside';
				else if (dismissEvent instanceof KeyboardEvent) dismissMethod = 'keyboard_escape';
				else dismissMethod = 'click_button';

				void onClosed({
					interactionIdentifier: 'dismiss',
					additionalMetadata: { dismissMethod, sourceComponent },
				});
				fireAnalyticsEvent(
					{
						eventType: 'ui',
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'jiraIssueViewProjectPlanTwcOnboardingSpotlightDismiss',
					},
					{ dismissMethod, sourceComponent },
				);
				dismiss();
			},
			[dismiss, onClosed, fireAnalyticsEvent, sourceComponent],
		);

		const handleTryNow = useCallback(() => {
			void onCtaActioned({
				interactionIdentifier: 'try-now',
				additionalMetadata: { actionTaken: true, sourceComponent },
			});
			fireAnalyticsEvent(
				{
					eventType: 'ui',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'jiraIssueViewProjectPlanTwcOnboardingSpotlightTryNow',
				},
				{ sourceComponent },
			);
			onClick?.();
			dismiss();
		}, [dismiss, onClick, onCtaActioned, fireAnalyticsEvent, sourceComponent]);

		const { isActive: isTriggerActive } = useTrigger(
			'jira-issue-view-project-plan-twc-onboarding-spotlight-trigger',
		);

		// Fire the "viewed" message-instance event and a UI 'shown' analytics
		// event exactly once when the spotlight first becomes visible. This
		// captures the rendering analytics with the originating sourceComponent
		// attribute so consumers can attribute the impression correctly.
		const hasFiredViewedEvent = useRef(false);
		useEffect(() => {
			if (!isTriggerActive || hasFiredViewedEvent.current) {
				return;
			}
			hasFiredViewedEvent.current = true;
			void messageInstanceViewed({ sourceComponent });
			fireAnalyticsEvent(
				{
					eventType: 'ui',
					action: 'shown',
					actionSubject: 'spotlight',
					actionSubjectId: 'jiraIssueViewProjectPlanTwcOnboardingSpotlight',
				},
				{ sourceComponent },
			);
		}, [isTriggerActive, messageInstanceViewed, fireAnalyticsEvent, sourceComponent]);

		if (!isTriggerActive) {
			return null;
		}

		return (
			<PopoverProvider>
				<PopoverTarget>
					{/* Dummy target */}
					<div />
				</PopoverTarget>
				<PopoverContent dismiss={handleDismiss} placement={placement} isVisible={true}>
					<SpotlightCard>
						<SpotlightHeader>
							<SpotlightHeadline>
								<FormattedMessage
									id="jira-issue-view-project-plan-twc-onboarding.spotlight.heading"
									defaultMessage="Capture work in a project doc"
									description="Spotlight heading, calling attention to the value of TWC Onboarding"
								/>
							</SpotlightHeadline>
							<SpotlightControls>
								<SpotlightDismissControl onClick={handleDismiss} />
							</SpotlightControls>
						</SpotlightHeader>
						<SpotlightMedia>
							<Image src={RovoSampleIllustrationLight} alt={formatMessage(imageAlt)} />
						</SpotlightMedia>
						<SpotlightBody>
							<Text>
								<FormattedMessage
									id="jira-issue-view-project-plan-twc-onboarding.spotlight.body"
									defaultMessage="Let Rovo AI create a doc to help you plan your project, capture new work items, and keep your team in the loop."
									description="Spotlight call to action, highlighting the value of Rovo AI."
								/>
							</Text>
						</SpotlightBody>
						<SpotlightFooter>
							<SpotlightActions>
								<SpotlightPrimaryAction onClick={handleTryNow}>
									<FormattedMessage
										id="jira-issue-view-project-plan-twc-onboarding.spotlight.try-now-button"
										defaultMessage="Try now"
										description="Primary action button that opens the twc onboarding modal"
									/>
								</SpotlightPrimaryAction>
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</PopoverContent>
			</PopoverProvider>
		);
	},
});

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<placementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
